// Endpoints exempt from the global 401/403 auto-logout in src/axiosSetup.ts.
// Kept in a side-effect-free module so tests can pin them without registering
// the axios interceptors.

// Endpoints that use 401/403 as a NORMAL signal (bad login creds, or the
// step-up "sudo" re-auth the audit-log sign-off flow depends on — see
// api/verification.ts: "the API answers 401 when re-auth is required, which the
// caller turns into the sudo modal"). Auto-logging-out on these would break
// login error handling and the sign-off step-up, so they are exempt — the
// calling code handles their 401/403 itself.
export const AUTH_SIGNAL_ENDPOINTS = /\/(SignIn|SessionVerify|ForgotPassword)(\/|$|\?)/i;

// Transactions-page actions receive business refusals as 403 (assignment-first
// guard, verifier identity, RBAC) that the page maps to gate modals / toasts —
// see components/Transactions/actionErrors.ts. Auto-logout on those kicks the
// admin out mid-action for a refusal that is working as designed, so they are
// exempt too. A genuinely dead token still logs out via the very next poll GET
// (/Transactions), which is NOT exempt.
//
// AuditTrails/auditor-approval belongs here for the same reason: it is a Part 11
// credential re-entry gate, so its 401 means "wrong password", not "dead token".
// Without the exemption a mistyped password on the sign-off form logs the auditor
// straight out instead of showing the failure — found 2026-08-23 in the rollout
// walkthrough, the same way the Transactions ones were found on 2026-08-09.
export const ACTION_REFUSAL_ENDPOINTS =
  /\/(DwellTimers\/[^/]+\/(start|confirm|edit-time)|AuditTrails\/(cosign-confirm|auditor-approval)|SessionVerifierReassign|WorkerMicroorganisms\/[^/]+\/plates\/\d+)(\/|$|\?)/i;

export const isLogoutExempt = (url: string): boolean =>
  AUTH_SIGNAL_ENDPOINTS.test(url) || ACTION_REFUSAL_ENDPOINTS.test(url);

// A refusal the SERVER labels as a role denial is never a dead session, whatever
// endpoint it came from. The Auditor read-only contract (middleware/auditorReadOnly.js)
// answers 403 with code 'auditor-read-only' on any write an Auditor attempts, and
// those can land on endpoints that must stay logout-eligible for everyone else —
// so match on the payload rather than widening the URL list. Found 2026-08-23:
// an Auditor clicking Reset Password on the Edit User page was logged out instead
// of being told no.
export const ROLE_REFUSAL_CODES = new Set(['auditor-read-only']);

export const isRoleRefusal = (data: any): boolean =>
  !!data && typeof data === 'object' && ROLE_REFUSAL_CODES.has((data as any).code);
